import { queryOptions } from "@tanstack/react-query";
import invariant from "tiny-invariant";
import { z } from "zod";

import { graphqlClient } from "@/api/graphql";
import { cropUnitSchema } from "@/api/types/digitalnisklady.cz/types";
import { entitySchema } from "@/common/schema/entity";
import { graphql } from "@/generated/digitalnisklady.cz";
import {
  CropCategory,
  CropSubcategory,
  Currency,
  MarketplaceQuery,
} from "@/generated/digitalnisklady.cz/graphql";

import {
  mapCropParameter,
  sortCropParameter,
} from "../mappings/crop-parameter";

// eslint-disable-next-line lingui/no-unlocalized-strings
const marketplaceDocument = graphql(`
  query Marketplace(
    $latitude: Float!
    $longitude: Float!
    $currency: Currency!
    $companyId: ID
    $locale: String
    $search: String
    $cropCategory: CropCategory
    $cropSubcategory: CropSubcategory
  ) {
    cropPricesInRegion(
      gps: { latitude: $latitude, longitude: $longitude }
      tradeType: TO_STORAGE
      companyId: $companyId
      search: $search
      cropCategory: $cropCategory
      cropSubcategory: $cropSubcategory
    ) {
      quarter {
        id
        name
      }
      crop {
        parameters {
          edges {
            node {
              id
              title
              ... on TextCropParameter {
                __typename
                title
                textDefault
              }
              ... on FloatCropParameter {
                __typename
                min
                max
                unit
              }
            }
          }
        }
        id
        name(locale: $locale)
        category
        subcategory
        unit
      }
      id
      price(currency: $currency)
    }
  }
`);

const marketplaceQuery = (variables: {
  search: string;
  latitude: number;
  longitude: number;
  currency: Currency;
  currentCategory: CropCategory | null;
  currentSubcategory: CropSubcategory | null;
  companyId: string | undefined;
  locale: string;
}) =>
  queryOptions({
    queryFn: () =>
      graphqlClient.request(marketplaceDocument, {
        companyId: variables.companyId,
        cropCategory: variables.currentCategory ?? undefined,
        cropSubcategory: variables.currentSubcategory ?? undefined,
        currency: variables.currency,
        latitude: variables.latitude,
        locale: variables.locale,
        longitude: variables.longitude,
        search: variables.search || undefined,
      }),
    queryKey: [
      "marketplace",
      variables.currency,
      variables.latitude,
      variables.longitude,
      variables.companyId,
      variables.locale,
      variables.search,
      variables.currentCategory,
      variables.currentSubcategory,
    ],
    select: (data) => selectMarketplace(data),
  });

const marketplaceItemSchema = z.object({
  crop: z.object({
    id: z.string(),
  }),
  id: z.string(),
  name: z.string(),
  parameters: z.array(z.array(z.string())),
  prices: z.array(
    z.object({
      id: z.string(),
      price: z.number(),
      quarter: entitySchema,
    }),
  ),
  subcategory: z.string().nullable().optional(),
  unit: cropUnitSchema,
});

type MarketplaceItem = z.infer<typeof marketplaceItemSchema>;
type Marketplace = Map<CropCategory, Array<MarketplaceItem>>;

const selectMarketplace = (data: MarketplaceQuery): Marketplace => {
  const output = new Map<CropCategory, Map<string, MarketplaceItem>>();

  data.cropPricesInRegion?.forEach((item) => {
    const category = item?.crop?.category;
    const cropName = item?.crop?.name;
    invariant(category && cropName);

    if (!output.has(category)) {
      output.set(category, new Map());
    }

    const categoryMap = output.get(category);
    invariant(categoryMap);

    if (!categoryMap.has(cropName)) {
      categoryMap.set(cropName, {
        category: item.crop?.category,
        crop: {
          // @ts-expect-error checked with zod
          id: item.crop?.id,
        },
        // @ts-expect-error checked with zod
        id: item.id,
        // @ts-expect-error checked with zod
        name: item.crop?.name,
        // @ts-expect-error checked with zod
        parameters: item.crop?.parameters?.edges
          ?.map(mapCropParameter)
          .toSorted(sortCropParameter),
        prices: [],
        subcategory: item.crop?.subcategory,
        // @ts-expect-error checked with zod
        unit: item.crop?.unit,
      });
    }

    const cropItem = categoryMap.get(cropName);
    invariant(cropItem);
    const existingPrice = cropItem.prices.find(
      (price) => price.quarter.id === item.quarter?.id,
    );

    if (!existingPrice) {
      cropItem.prices.push({
        id: item.id!,
        price: item.price!,
        // @ts-expect-error checked with zod
        quarter: item.quarter,
      });
      // @ts-expect-error checked with zod
    } else if (item.price < existingPrice.price) {
      // @ts-expect-error checked with zod
      existingPrice.price = item.price;
    }
  });

  const result = new Map(
    Array.from(output.entries()).map(([outerKey, innerMap]) => [
      outerKey,
      Array.from(innerMap.values()),
    ]),
  );

  return z
    .map(z.enum(CropCategory), z.array(marketplaceItemSchema))
    .parse(result);
};

export { marketplaceQuery };
export type { MarketplaceItem };
